import { Link } from "react-router-dom";

export interface ArticleCardData {
  id: string;
  title: string;
  summary: string;
  category: string;
  categorySlug: string;
  cover: string;
  timeLabel: string;
  views: number;
  comments: number;
  tags: string[];
}

interface ArticleCardProps {
  article: ArticleCardData;
  variant?: "default" | "compact" | "wide";
}

function formatCount(value: number) {
  if (value >= 10000) return `${(value / 10000).toFixed(1)} 万`;
  return `${value}`;
}

export default function ArticleCard({ article, variant = "default" }: ArticleCardProps) {
  if (variant === "compact") {
    return (
      <article className="group flex gap-3 border-b border-background-200 pb-3 last:border-0 last:pb-0">
        <Link
          to={`/article/${article.id}`}
          className="relative h-20 w-24 shrink-0 overflow-hidden rounded-md md:w-28"
        >
          <img
            src={article.cover}
            alt={article.title}
            title={`${article.title} - 看瓜爆料`}
            className="h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
          />
        </Link>
        <div className="min-w-0">
          <Link to={`/category/${article.categorySlug}`} className="text-xs font-medium text-primary-600">
            {article.category}
          </Link>
          <h3 className="mt-1 text-sm leading-snug">
            <Link
              to={`/article/${article.id}`}
              className="line-clamp-2 font-heading font-semibold text-foreground-900 transition-colors group-hover:text-primary-700"
            >
              {article.title}
            </Link>
          </h3>
          <p className="mt-1.5 text-xs text-foreground-500">
            {article.timeLabel} · {formatCount(article.views)} 阅读
          </p>
        </div>
      </article>
    );
  }

  if (variant === "wide") {
    return (
      <article className="group grid grid-cols-1 gap-4 overflow-hidden rounded-lg border border-background-200 bg-background-50 p-3 md:grid-cols-12 md:p-4">
        <Link
          to={`/article/${article.id}`}
          className="relative h-44 w-full overflow-hidden rounded-md md:col-span-5 md:h-full"
        >
          <img
            src={article.cover}
            alt={article.title}
            title={`${article.title} - 看瓜爆料`}
            className="h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
          />
        </Link>
        <div className="flex flex-col md:col-span-7 md:py-1">
          <div className="flex items-center gap-2">
            <Link
              to={`/category/${article.categorySlug}`}
              className="rounded-full bg-secondary-100 px-2.5 py-1 text-xs font-medium text-secondary-900"
            >
              {article.category}
            </Link>
            <span className="text-xs text-foreground-500">{article.timeLabel}</span>
          </div>
          <h3 className="mt-3 font-heading text-lg font-bold leading-snug">
            <Link
              to={`/article/${article.id}`}
              className="text-foreground-950 transition-colors group-hover:text-primary-700"
            >
              {article.title}
            </Link>
          </h3>
          <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">
            {article.summary}
          </p>
          <div className="mt-auto flex flex-wrap items-center gap-3 pt-4 text-xs text-foreground-500">
            {article.tags.slice(0, 2).map((tag) => (
              <span key={tag} className="rounded-md border border-background-200 px-2 py-0.5">
                {tag}
              </span>
            ))}
            <span className="flex items-center gap-1">
              <i className="ri-eye-line" aria-hidden="true"></i>
              {formatCount(article.views)}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-chat-3-line" aria-hidden="true"></i>
              {formatCount(article.comments)}
            </span>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300">
      <Link to={`/article/${article.id}`} className="relative block h-44 w-full overflow-hidden">
        <img
          src={article.cover}
          alt={article.title}
          title={`${article.title} - 看瓜爆料`}
          className="h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute left-3 top-3 rounded-full bg-background-50/95 px-2.5 py-1 text-xs font-medium text-primary-700">
          {article.category}
        </span>
      </Link>
      <div className="flex flex-1 flex-col p-4">
        <h3 className="font-heading text-base font-bold leading-snug">
          <Link
            to={`/article/${article.id}`}
            className="line-clamp-2 text-foreground-950 transition-colors group-hover:text-primary-700"
          >
            {article.title}
          </Link>
        </h3>
        <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">
          {article.summary}
        </p>
        <div className="mt-auto flex items-center justify-between pt-4 text-xs text-foreground-500">
          <span>{article.timeLabel}</span>
          <span className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <i className="ri-eye-line" aria-hidden="true"></i>
              {formatCount(article.views)}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-chat-3-line" aria-hidden="true"></i>
              {formatCount(article.comments)}
            </span>
          </span>
        </div>
      </div>
    </article>
  );
}